import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Search, XCircle } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import { GetMessagesQuery as GetMessages } from '__generated__/graphql';
import { assetUrl, isGraphQLValidationError } from '../../../common/helpers';
import AddMessageForm from './AddMessageForm';
import EventPicker from '../../Events/EventPicker';
import GetMessagesQuery from './GetMessagesQuery';
import MessageStatusLabel from './MessageStatusLabel';
import UI from '../../../common/UI';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useUrlState from '../../../common/useUrlState';

type Message = GetMessages['project']['messages']['data'][0];

interface MessageFilters {
  event?: string;
  search?: string;
}

interface UrlFilters extends MessageFilters {
  page?: number;
}

const MessagesPage = () => {
  const { t } = useTranslation();
  const project = useProject();
  const history = useHistory();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('messages'));

  const [{ page, ...filters }, setUrlState] = useUrlState<UrlFilters>({
    parse: (params, { string, integer }) => ({
      event: string(params.event),
      search: string(params.search),
      page: integer(params.page, 1),
    }),
  });

  /** Replaces filters in the URL */
  const handleFiltersChange = (update: (filters: MessageFilters) => MessageFilters) => {
    setUrlState(({ page, ...urlState }) => update({ ...urlState, ...filters }), true);
  };

  const { data, loading } = useQuery(GetMessagesQuery, {
    variables: {
      project_id: project.id,
      page,
      per_page: 20,
      ...omit(filters, 'event'),
      events: filters.event ? [filters.event] : undefined,
    },
    onError: (error) => {
      if (isGraphQLValidationError(error)) {
        // Recent event no longer exists, reset it.
        recentEvent.reset();
      }
    },
    fetchPolicy: 'cache-and-network',
  });

  const hasFilters = !!filters.event || !!filters.search;

  const messages = data?.project.messages;

  const [showEvent, setShowEvent] = useState(!filters.event);
  const messagesRef = useRef(messages);

  // Show event labels after new messages have been retrieved.
  useEffect(() => {
    if (messagesRef.current !== messages) {
      setShowEvent(!filters.event);
    }
    messagesRef.current = messages;
  }, [messages, filters.event]);

  const [openAddMessageModal, closeAddMessageModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeAddMessageModal}>
      <AddMessageForm
        eventId={filters.event}
        onSuccess={(message) => history.push(`/${project.id}/communication/messages/${message.id}`)}
      />
    </UI.Modal>
  ), [filters.event]);

  if (!data) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer sc={{ columns: '1fr 1fr', mb: 4, alignVertical: 'center' }}>
        <UI.Div>
          <UI.SectionHeading sc={{ mb: 0 }}>
            {t('messages')}
          </UI.SectionHeading>
        </UI.Div>
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            onClick={() => openAddMessageModal()}
            sc={{ brand: 'secondary' }}
          >
            {t('add_message')}
          </UI.Button>
        </UI.Div>
      </UI.GridContainer>

      <UI.GridContainer
        sc={{ columns: ['1fr', '1fr 3fr'] }}
      >
        <UI.Div>
          <FilterBar filters={filters} onChange={handleFiltersChange} />
        </UI.Div>

        <UI.Div>
          <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
            {messages.data.map((message) => (
              <MessageCard message={message} showEvent={showEvent} key={message.id} />
            ))}

            {messages.total === 0 && (
              <>
                {hasFilters && (
                  <UI.Card
                    sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                    style={{ textWrap: 'balance' }}
                  >
                    <UI.Div>
                      <img src={assetUrl('images/communicate.svg')} alt="" height={120} />
                    </UI.Div>
                    {t('no_messages_match_filters')}
                    {' '}
                    <UI.Link to={`/${project.id}/communication/messages`}>{t('reset_filters')}</UI.Link>
                  </UI.Card>
                )}

                {!hasFilters && !loading && (
                  <UI.Card
                    sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                    style={{ textWrap: 'balance' }}
                  >
                    <img src={assetUrl('images/communicate.svg')} alt="" height={120} />
                    <UI.H3 sc={{ mb: 2 }}>{t('no_messages_yet')}</UI.H3>
                    <UI.Paragraph sc={{ mb: 0 }}>
                      {t('no_messages_get_started')}
                    </UI.Paragraph>
                  </UI.Card>
                )}
              </>
            )}

            {messages && messages.last_page > 1 && (
              <UI.Div sc={{ mt: 4 }}>
                <UI.Pager pageCount={messages.last_page} />
              </UI.Div>
            )}
          </UI.GridContainer>
        </UI.Div>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface FilterBarProps {
  filters: MessageFilters;
  onChange: Dispatch<SetStateAction<MessageFilters>>;
}

const FilterBar = ({ filters, onChange }: FilterBarProps) => {
  const { t } = useTranslation();

  const searchRef = useRef<HTMLInputElement>();

  /**
   * Handles changes to the search input by storing the value in the params.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((filters) => ({ ...filters, search: event.target.value || null }));
  };

  /**
   * Unsets the search term in the params and empties the input.
   */
  const resetSearch = () => {
    onChange((filters) => ({ ...filters, search: '' }));

    searchRef.current.value = '';
  };

  return (
    <UI.FormGrid>
      <UI.InputGroup>
        <EventPicker
          eventId={filters.event}
          onChange={(event) => onChange((filters) => ({ ...filters, event: event?.id }))}
          renderActive={(event) => (
            <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
              {event?.title || t('all_events')}
            </UI.Span>
          )}
        />
      </UI.InputGroup>

      <UI.InputGroup>
        <UI.InputWrapper>
          <UI.Span>
            <UI.Icon>
              <Search />
            </UI.Icon>
          </UI.Span>
          <UI.DebouncedInput
            type="text"
            value={filters.search}
            onChange={handleSearch}
            placeholder={t('search_messages')}
            ref={searchRef}
            sc={{ px: 5 }}
          />
          {filters.search && (
            <UI.Span>
              <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                <UI.Icon>
                  <XCircle />
                </UI.Icon>
              </UI.A>
            </UI.Span>
          )}
        </UI.InputWrapper>
      </UI.InputGroup>
    </UI.FormGrid>
  );
};

interface MessageCardProps {
  message: Message;
  showEvent?: boolean;
}

export const MessageCard = ({ message, showEvent }: MessageCardProps) => {
  const project = useProject();

  const to = `/${project.id}/communication/messages/${message.id}`;

  return (
    <UI.NavCard to={to} title={message.title}>
      <UI.GridContainer sc={{ columns: ['1fr', '3fr 3fr 2fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div style={{ minWidth: 0, gridColumn: !showEvent ? '1 / span 2' : undefined }}>
          <UI.H4>
            <UI.Truncate>
              {message.title}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        {showEvent && (
          <UI.FlexContainer sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
            <UI.Label sc={{ small: true }} style={{ minWidth: 0 }}>
              <UI.Truncate>
                {message.event.title}
              </UI.Truncate>
            </UI.Label>
          </UI.FlexContainer>
        )}
        <UI.FlexContainer sc={{ justifyContent: 'end' }}>
          <MessageStatusLabel message={message} />
        </UI.FlexContainer>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

export default MessagesPage;
